/**
 * Leitura de CSV.
 *
 * Escrito à mão, e não com biblioteca, por um motivo concreto: o que chega do sistema de
 * compras brasileiro tem três armadilhas previsíveis — delimitador `;`, acentuação em
 * Latin-1 e campo entre aspas com o próprio delimitador dentro. Um `split(',')` erra nas
 * três. As trinta linhas abaixo acertam, e o sistema continua sem dependência nova.
 */

const BOM_UTF8 = [0xef, 0xbb, 0xbf];

/**
 * Descobre a codificação do arquivo.
 *
 * Sem BOM, tenta UTF-8 estrito: se falhar, o arquivo é Latin-1 — que é o que o Excel
 * brasileiro ainda exporta por padrão. Decodificar Latin-1 como UTF-8 produziria
 * "Tijolo cerÃ¢mico", e o usuário levaria a culpa por um erro que não cometeu.
 */
export function decodeText(bytes: Uint8Array): string {
  const hasBom = BOM_UTF8.every((byte, index) => bytes[index] === byte);
  const body = hasBom ? bytes.subarray(3) : bytes;

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(body);
  } catch {
    return new TextDecoder('windows-1252').decode(body);
  }
}

/** Delimitador mais frequente na primeira linha — `;` empata com `,` só em arquivo vazio. */
export function detectDelimiter(sample: string): string {
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? '';
  const candidates = [';', ',', '\t', '|'];
  let best = ',';
  let bestCount = 0;

  for (const candidate of candidates) {
    // Conta apenas fora de aspas: "Tijolo 19x30, vermelho" não é duas colunas.
    let count = 0;
    let inQuotes = false;
    for (let index = 0; index < firstLine.length; index += 1) {
      const char = firstLine[index];
      if (char === '"') inQuotes = !inQuotes;
      else if (char === candidate && !inQuotes) count += 1;
    }
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }

  return best;
}

/** Divide o texto em células, respeitando aspas, aspas escapadas e quebra dentro do campo. */
export function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let index = 0;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  while (index < text.length) {
    const char = text[index]!;

    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        inQuotes = false;
        index += 1;
        continue;
      }
      field += char;
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      index += 1;
      continue;
    }
    if (char === delimiter) {
      endField();
      index += 1;
      continue;
    }
    if (char === '\r') {
      index += 1;
      continue;
    }
    if (char === '\n') {
      endRow();
      index += 1;
      continue;
    }

    field += char;
    index += 1;
  }

  if (field !== '' || row.length > 0) endRow();

  // Linha totalmente vazia é ruído de fim de arquivo, não registro.
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}
